import type { QueryKey } from "@tanstack/react-query";

import type { ResourceRef } from "@mountaya/client";

import { activitiesQueryKey, layersQueryKey } from "./catalogKeys.js";
import { mountayaKeys } from "./mountayaKeys.js";

/**
 * Where a studio resource lives in a TanStack query cache.
 *
 * It stays HERE, with the reads that cache under it, while the `StudioCache` seam it serves is
 * typed in `@mountaya/studio` and implemented over TanStack in `@mountaya/studio-react`. The split
 * is the invariant: invalidating `{ kind: "itinerary", id }` has to drop the entry `getItinerary`
 * wrote, and it can only do that if ONE module decides both. This is that decision, so it belongs
 * beside the reads rather than beside the seam.
 *
 * `ResourceRef` is named for its type alone and erases, so this adds no runtime edge from a
 * transport binding to a studio. Nothing here imports React either, so
 * `@mountaya/client-react/query` publishes it to a server.
 */
/**
 * listingKey is where a listing is addressed: the operation's key, narrowed by the filters a ref
 * names.
 *
 * A walk of the listing caches under the operation's key for its whole query, followed by a
 * segment of its own (`mountayaInfiniteQueryOptions`), so this key is a PREFIX of every walk it
 * names: with no filters, of every walk of the operation; with filters, of every walk whose
 * query holds at least those, whatever else it was filtered, sorted or paged by, through
 * TanStack's partial matching of the query object.
 */
const listingKey = (
  scope: string,
  operationId: "getCollections" | "getItineraries" | "getZones",
  filters: Readonly<Record<string, unknown>> | undefined,
): QueryKey =>
  filters === undefined
    ? mountayaKeys.operation(scope, "rest", operationId)
    : mountayaKeys.operation(scope, "rest", operationId, { query: filters });

/**
 * studioResourceKey is how a resource is addressed in the cache, as the operation whose read it
 * is the answer to.
 *
 * Exported because the builders in `studioQueryOptions.ts` read under it. That is what makes the
 * correspondence `StudioCache` depends on STRUCTURAL rather than asserted: invalidating
 * `{ kind: "itinerary", id }` drops the entry `useItinerary` wrote because both resolve the key
 * here, not because a test compares two restatements that a rename could take apart one at a time.
 *
 * @param scope The credential scope of the client that reads the resource, its
 *   `transport.credentialScope`. The activity catalog's key ignores it.
 * @param key The resource.
 * @returns The key the resource's read caches under, or, for a key that leaves its narrowing
 *   out, the prefix of every such key.
 */
export const studioResourceKey = (scope: string, key: ResourceRef): QueryKey => {
  switch (key.kind) {
    case "activities":
      // The catalog is scoped to the CATALOG rather than to the credential, because it is the
      // same list for everyone. `catalogKeys.ts` owns that decision; reading its key back is what
      // stops this file from making a second one.
      return activitiesQueryKey();
    case "collection":
      return mountayaKeys.operation(scope, "rest", "getCollection", { collection_id: key.id });
    case "collectionItineraries":
      // A collection's contents are a listing read a page at a time, and its walk caches under
      // this key followed by its query, so the key names every walk of the one collection, and
      // without an id the operation's key names every collection's.
      return key.id === undefined
        ? mountayaKeys.operation(scope, "rest", "getCollectionItineraries")
        : mountayaKeys.operation(scope, "rest", "getCollectionItineraries", {
            collection_id: key.id,
          });
    case "collectionZones":
      return key.id === undefined
        ? mountayaKeys.operation(scope, "rest", "getCollectionZones")
        : mountayaKeys.operation(scope, "rest", "getCollectionZones", { collection_id: key.id });
    case "collectionSpecification":
      // A specification is a TILE read, not a REST one, and the surface is the segment that
      // says so: invalidating `surface("rest")` must not drop the style a map is drawing from.
      // A read that names the layers it asks for caches under its query, after the id, so the
      // key without one names every read of the collection, and the key without an id every
      // collection's.
      return key.id === undefined
        ? mountayaKeys.operation(scope, "tiles", "getCollectionSpecification")
        : mountayaKeys.operation(scope, "tiles", "getCollectionSpecification", {
            collection_id: key.id,
            ...(key.query === undefined ? {} : { query: key.query }),
          });
    case "collections":
      return listingKey(scope, "getCollections", key.filters);
    case "itinerary":
      // The published record is a SEPARATE entry, keyed by the query that asks for it, because
      // the editor holds the draft under the bare key: folding them together would let a wizard
      // reading the publication overwrite what the editor is showing.
      return key.surface === "published"
        ? mountayaKeys.operation(scope, "rest", "getItinerary", {
            itinerary_id: key.id,
            query: { surface: "published" },
          })
        : mountayaKeys.operation(scope, "rest", "getItinerary", { itinerary_id: key.id });
    case "itineraries":
      return listingKey(scope, "getItineraries", key.filters);
    case "itinerarySpecification":
      return mountayaKeys.operation(scope, "tiles", "getItinerarySpecification", {
        itinerary_id: key.id,
      });
    case "layers":
      // The manifest is narrowed to its credential and to the scope it names, so its entry is
      // the one `useLayers` reads under. `catalogKeys.ts` owns that key, as it owns the activity
      // catalog's, and a named scope appends to it, so the family is a prefix of every scope's.
      return layersQueryKey(scope, key.organizationId);
    case "zone":
      // The published record is a SEPARATE entry, as an itinerary's is, so a surface reading
      // what the public is served never overwrites the draft the editor holds.
      return key.surface === "published"
        ? mountayaKeys.operation(scope, "rest", "getZone", {
            query: { surface: "published" },
            zone_id: key.id,
          })
        : mountayaKeys.operation(scope, "rest", "getZone", { zone_id: key.id });
    case "zones":
      return listingKey(scope, "getZones", key.filters);
  }
};
